import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, InputNumber,
  Tag, Space, message, Typography, Row, Col, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/packages');
      if (response.data.success) {
        setPackages(response.data.data || []);
      } else {
        message.error('Failed to load packages');
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      message.error(error.response?.data?.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    form.resetFields();
    form.setFieldsValue({ gstRate: 18, maxDiscountPercent: 20 });
    setModalVisible(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    form.setFieldsValue({
      name: pkg.name,
      description: pkg.description || '',
      userLimit: pkg.userLimit,
      price: parseFloat(pkg.price),
      gstRate: parseFloat(pkg.gstRate),
      maxDiscountPercent: parseFloat(pkg.maxDiscountPercent)
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingPackage(null);
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      if (editingPackage) {
        const response = await api.put(`/packages/${editingPackage.id}`, values);
        if (response.data.success) {
          message.success('Package updated successfully!');
          closeModal();
          fetchPackages();
        } else {
          message.error(response.data.message || 'Update failed');
        }
      } else {
        const response = await api.post('/packages', values);
        if (response.data.success) {
          message.success('Package created successfully!');
          closeModal();
          fetchPackages();
        } else {
          message.error(response.data.message || 'Create failed');
        }
      }
    } catch (error) {
      console.error('Error saving package:', error);
      message.error(error.response?.data?.message || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (pkg) => {
    Modal.confirm({
      title: `Delete "${pkg.name}"?`,
      content: `This package will be permanently deleted. Companies using it will lose their package link.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/packages/${pkg.id}`);
          if (response.data.success) {
            message.success('Package deleted successfully');
            fetchPackages();
          } else {
            message.error(response.data.message);
          }
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to delete package');
        }
      }
    });
  };

  // Calculate final price with GST
  const calcFinalPrice = (price, gstRate) => {
    if (!price || !gstRate) return price || 0;
    return (price * (1 + gstRate / 100)).toFixed(2);
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{index + 1}</Text>
      )
    },
    {
      title: 'Package Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <strong style={{ fontSize: 14 }}>{text}</strong>
          {record.description && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'User Limit',
      dataIndex: 'userLimit',
      key: 'userLimit',
      align: 'center',
      render: (val) => (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: 13 }}>
          {val} Users
        </Tag>
      )
    },
    {
      title: 'Base Price (₹)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (val) => (
        <Text strong>₹{parseFloat(val).toLocaleString('en-IN')}</Text>
      )
    },
    {
      title: 'GST',
      dataIndex: 'gstRate',
      key: 'gstRate',
      align: 'center',
      render: (val) => (
        <Tag color="orange">{parseFloat(val)}%</Tag>
      )
    },
    {
      title: 'Final Price (₹)',
      key: 'finalPrice',
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ color: '#1890ff' }}>
          ₹{parseFloat(calcFinalPrice(record.price, record.gstRate)).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Companies',
      dataIndex: 'companyCount',
      key: 'companyCount',
      align: 'center',
      render: (val) => (
        <Badge
          count={val || 0}
          showZero
          style={{ backgroundColor: val > 0 ? '#52c41a' : '#d9d9d9' }}
        />
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      render: (val) => val ? (
        <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
      ) : (
        <Tag icon={<CloseCircleOutlined />} color="error">Inactive</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Package">
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Package">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              disabled={record.companyCount > 0}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Package Management</Title>
          <Text type="secondary">Manage subscription packages for companies</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchPackages} loading={loading} />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              size="large"
              style={{ background: '#1890ff' }}
            >
              Add Package
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small" style={{ borderLeft: '4px solid #1890ff', borderRadius: 6 }}>
            <Text type="secondary">Total Packages</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{packages.length}</div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderLeft: '4px solid #52c41a', borderRadius: 6 }}>
            <Text type="secondary">Active Packages</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>
              {packages.filter(p => p.isActive).length}
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderLeft: '4px solid #722ed1', borderRadius: 6 }}>
            <Text type="secondary">Total Companies</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>
              {packages.reduce((sum, p) => sum + (p.companyCount || 0), 0)}
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderLeft: '4px solid #fa8c16', borderRadius: 6 }}>
            <Text type="secondary">Max User Limit</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>
              {packages.length > 0 ? Math.max(...packages.map(p => p.userLimit)) : 0}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Packages Table */}
      <Card style={{ borderRadius: 8 }}>
        <Table
          dataSource={packages}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} packages`
          }}
          locale={{ emptyText: 'No packages found. Click "Add Package" to create one.' }}
          rowClassName={(record) => !record.isActive ? 'inactive-row' : ''}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {editingPackage ? `✏️ Edit: ${editingPackage.name}` : '➕ Create New Package'}
          </span>
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="Package Name"
            name="name"
            rules={[
              { required: true, message: 'Package name is required' },
              { min: 3, message: 'At least 3 characters' }
            ]}
          >
            <Input placeholder="e.g., 5 Users Plan" size="large" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea
              rows={2}
              placeholder="Package description (optional)"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="User Limit"
                name="userLimit"
                rules={[{ required: true, message: 'User limit is required' }]}
              >
                <InputNumber
                  min={1} max={1000}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="e.g., 5"
                  addonAfter="Users"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Base Price (₹)"
                name="price"
                rules={[{ required: true, message: 'Price is required' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="e.g., 1999"
                  formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={v => v.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="GST Rate (%)" name="gstRate">
                <InputNumber
                  min={0} max={50}
                  style={{ width: '100%' }}
                  size="large"
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Max Discount (%)" name="maxDiscountPercent">
                <InputNumber
                  min={0} max={50}
                  style={{ width: '100%' }}
                  size="large"
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 Final Price = Base Price + GST. Shown to customers after applying discount.
            </Text>
          </div>

          <Row justify="end">
            <Space>
              <Button size="large" onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={saving}
                icon={editingPackage ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>

      <style>{`
        .inactive-row { opacity: 0.6; background: #fafafa; }
      `}</style>
    </div>
  );
};

export default PackageManagement;
